'use client';

import { useRef, useEffect, useCallback } from 'react';
import { drawFrame } from './ai-glow-border.renderer';
import {
  PHASE_DURATION_MS,
  OPACITY_PULSE_DURATION_MS,
  OPACITY_MIN,
  OPACITY_MAX,
  TWO_PI,
} from './ai-glow-border.types';

interface AIGlowBorderProps {
  isActive: boolean;
  compact?: boolean;
}

/**
 * Animated braid glow border flowing along the image perimeter.
 * Canvas 2D overlay with rAF loop — no React re-renders.
 * 1:1 port from Android AIGlowBorder.
 */
export function AIGlowBorder({ isActive, compact = false }: AIGlowBorderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  const animate = useCallback(
    (now: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      if (startTimeRef.current === 0) startTimeRef.current = now;
      const elapsed = now - startTimeRef.current;

      // Animation phase: 0 -> 2*PI over PHASE_DURATION_MS, then restart
      const animationPhase = (elapsed / PHASE_DURATION_MS) * TWO_PI;

      // Opacity pulse: triangle wave with EaseInOut smoothstep, 3.5s period
      const pulseT =
        (elapsed % OPACITY_PULSE_DURATION_MS) / OPACITY_PULSE_DURATION_MS;
      // Triangle wave: 0->1->0 over one period
      const triangle = pulseT < 0.5 ? pulseT * 2 : 2 - pulseT * 2;
      // Smoothstep EaseInOut
      const smooth = triangle * triangle * (3 - 2 * triangle);
      const opacityPulse = OPACITY_MIN + (OPACITY_MAX - OPACITY_MIN) * smooth;

      // Resize canvas to match CSS size at device pixel ratio
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = rect.width;
      const h = rect.height;
      const canvasW = Math.round(w * dpr);
      const canvasH = Math.round(h * dpr);

      if (canvas.width !== canvasW || canvas.height !== canvasH) {
        canvas.width = canvasW;
        canvas.height = canvasH;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawFrame(ctx, w, h, animationPhase, opacityPulse, compact);

      rafRef.current = requestAnimationFrame(animate);
    },
    [compact]
  );

  useEffect(() => {
    if (!isActive) return;

    // Check prefers-reduced-motion
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;

    startTimeRef.current = 0;
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, animate]);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-40"
      aria-hidden="true"
    />
  );
}
